import React, {useRef, useState} from 'react';
import {
    TextField,
    Button,
    Typography,
    Box,
    Autocomplete,
    Card,
    CardHeader,
    CardContent,
} from '@mui/material';

import { _airlines_list } from '../../../sampleData/airline_data';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import dayjs from "dayjs";
import {DatePicker} from "@mui/x-date-pickers";


const FlightInputSearch = ({ onSearch, flightMode, setFlightMode }) => {

    const { PA_GlobalObject} = usePA_GlobalStore();

    const [eventHasDates] = useState(PA_GlobalObject.Planning.time_table?.dates?.length > 0);
    const eventDates = PA_GlobalObject.Planning.time_table?.dates?.map(d => dayjs(d.date)) || [];
    const lastDate = eventDates.length > 0
        ? eventDates.reduce((latest, current) => current.isAfter(latest) ? current : latest)
        : null;

    const [airlineCode, setAirlineCode] = useState('');
    const [airlineName, setAirlineName] = useState('');
    const [flightNumber, setFlightNumber] = useState('');
    const [date, setDate] = useState(eventHasDates ? PA_GlobalObject.Planning.time_table?.dates[0].date : () => new Date().toISOString().split('T')[0]);


    const handleAirlineChange = (event, newValue) => {
        if (newValue) {
            const [name, code] = newValue.split(' - ');
            setAirlineName(name);
            setAirlineCode(code);
        }
    };

    const handleFlightModeChange = (event, newValue) => {
        setFlightMode(newValue);
        //if there are event dates then use the first date or the last date
        if (eventHasDates){
            if (newValue === "getting_there"){
                setDate(PA_GlobalObject.Planning.time_table?.dates[0].date);
            } else {
                setDate(PA_GlobalObject.Planning.time_table?.dates[PA_GlobalObject.Planning.time_table?.dates.length-1].date);
            }
        }
    };

    const flightNumberRef = useRef(null);
    const handleConnecting = () => {
        /*
            leave the airline and date but clear out the flight number and put the cursor in the flight number input
         */
        setFlightNumber("");
        flightNumberRef.current?.focus();
    };

    const handleSubmit = () => {
        if (airlineCode && flightNumber && date) {
            onSearch({ airlineCode, flightNumber, date, flightMode });
        }
    };

    return (

        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
        }}>
            <Card sx={{
                width: '100%',
                p: 1,
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                }}>
                    <CardHeader
                        title={
                            <Typography
                                variant="h5"
                                component="div"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#1a237e', // Deep blue
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                {flightMode === 'getting_there' ? 'Getting There ✈️' : 'Getting Back 🏠'}
                            </Typography>
                        }
                        subheader={
                            <Typography variant="body2" sx={{color: '#616161'}}>
                                Find your flight information with ease.
                            </Typography>
                        }
                        sx={{p: 0}}
                    />
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        p: '4px',
                    }}>
                        <Button
                            variant="contained"
                            onClick={(e) => handleFlightModeChange(e, flightMode === 'getting_there' ? 'getting_back' : 'getting_there')}
                            sx={{
                                borderRadius: 6,
                                textTransform: 'none',
                                fontWeight: 'medium',
                            }}
                        >
                            Add {flightMode === 'getting_there' ? 'Getting Back' : 'Getting There'} Flights
                        </Button>
                    </Box>
                </Box>

                <CardContent sx={{p: '0 !important'}}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(4, 1fr)',
                            },
                            gap: 2,
                            alignItems: 'flex-end',
                        }}
                    >
                        <Autocomplete
                            value={airlineCode ? `${airlineName} - ${airlineCode}` : ''}
                            onChange={handleAirlineChange}
                            options={_airlines_list}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => <TextField {...params} label="Airline" fullWidth variant="outlined"/>}
                            sx={{flexGrow: 1, minWidth: '200px'}}
                        />

                        <TextField
                            label="Flight Number"
                            value={flightNumber}
                            onChange={(e) => setFlightNumber(e.target.value)}
                            sx={{backgroundColor: '#ffffff'}}
                            inputRef={flightNumberRef}
                        />

                        <DatePicker
                            label="Date"
                            value={dayjs(date, 'YYYY-MM-DD')}
                            onChange={(e)=> setDate(e.format('YYYY-MM-DD'))}
                            maxDate={lastDate || undefined} // if no event dates, no max
                        />

                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{height: '56px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontWeight: 'bold'}}
                        >
                            Search
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="text"
                            onClick={handleConnecting}
                            sx={{
                                borderRadius: 4,
                                textTransform: 'none',
                                fontWeight: 'medium',
                                color: '#29b6f6',
                                p: '4px 8px',
                                fontSize: '0.875rem'
                            }}
                        >
                            Add Connecting
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>

    );
};


export default FlightInputSearch;
